/*
 * แต่งภาพ (round 1): light/colour adjustments and ready-made looks for a picture, without touching the picture itself.
 * The settings live on the picture as `photoEdit` (saved with the work, EXTRA_PROPS); the filters Fabric draws with
 * are made from them by filterSpecs() — one source, so the screen, PNG/PDF and video all draw the same thing, and
 * "คืนค่ารูปเดิม" is just clearing the settings. Pure maths here (tests/photo.test.ts); the sheet is src/ui/photoSheet.ts.
 */

export interface PhotoEdit {
  /** −100…100 */
  brightness: number; contrast: number; saturation: number;
  /** −100 (cool) … 100 (warm) */
  warmth: number;
  /** 0…100 */
  sharpness: number; blur: number;
  /** a look from PRESETS ('' = none) and how strong it is, 0…100 */
  preset: string; amount: number;
}

export const NO_EDIT: PhotoEdit = { brightness: 0, contrast: 0, saturation: 0, warmth: 0, sharpness: 0, blur: 0, preset: '', amount: 100 };

type Matrix = number[];   // Fabric ColorMatrix: 4×5, the 5th column is an offset in 0…1
const I: Matrix = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0];
/** saturation s (1 = unchanged, 0 = grey), luminance weights Rec. 709 */
const sat = (s: number): Matrix => {
  const r = 0.2126 * (1 - s), g = 0.7152 * (1 - s), b = 0.0722 * (1 - s);
  return [r + s, g, b, 0, 0, r, g + s, b, 0, 0, r, g, b + s, 0, 0, 0, 0, 0, 1, 0];
};
/** warm (t > 0) / cool (t < 0) */
const temp = (t: number): Matrix => [1 + 0.12 * t, 0, 0, 0, 0.02 * t, 0, 1 + 0.03 * t, 0, 0, 0, 0, 0, 1 - 0.12 * t, 0, -0.02 * t, 0, 0, 0, 1, 0];
/** contrast c around mid-grey */
const con = (c: number): Matrix => { const o = 0.5 * (1 - c); return [c, 0, 0, 0, o, 0, c, 0, 0, o, 0, 0, c, 0, o, 0, 0, 0, 1, 0]; };
/** a × b (apply b, then a) for 4×5 colour matrices */
export function mulMatrix(a: Matrix, b: Matrix): Matrix {
  const out: number[] = [];
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 5; c++) {
      let v = 0;
      for (let k = 0; k < 4; k++) v += a[r * 5 + k] * b[k * 5 + c];
      if (c === 4) v += a[r * 5 + 4];
      out.push(v);
    }
  }
  return out;
}

/** The ready-made looks (choice ก: about ten), Thai names for the sheet. Matrices from Fabric's own presets where it has one. */
export const PRESETS: Array<{ id: string; name: string; matrix: Matrix }> = [
  { id: 'bw', name: 'ขาวดำ', matrix: sat(0) },
  { id: 'sepia', name: 'ซีเปีย', matrix: [0.393, 0.769, 0.189, 0, 0, 0.349, 0.686, 0.168, 0, 0, 0.272, 0.534, 0.131, 0, 0, 0, 0, 0, 1, 0] },
  { id: 'vintage', name: 'วินเทจ', matrix: [0.62793, 0.32021, -0.03965, 0, 0.03784, 0.02578, 0.64411, 0.03259, 0, 0.02926, 0.0466, -0.08512, 0.52416, 0, 0.02023, 0, 0, 0, 1, 0] },
  { id: 'film', name: 'ฟิล์ม', matrix: [1.12855, -0.39673, -0.03992, 0, 0.24991, -0.16404, 1.08352, -0.05498, 0, 0.09698, -0.16786, -0.56034, 1.60148, 0, 0.13972, 0, 0, 0, 1, 0] },
  { id: 'polaroid', name: 'โพลารอยด์', matrix: [1.438, -0.062, -0.062, 0, 0, -0.122, 1.378, -0.122, 0, 0, -0.016, -0.016, 1.483, 0, 0, 0, 0, 0, 1, 0] },
  { id: 'warm', name: 'อบอุ่น', matrix: mulMatrix(temp(0.8), sat(1.1)) },
  { id: 'cool', name: 'เย็น', matrix: mulMatrix(temp(-0.8), sat(0.95)) },
  { id: 'vivid', name: 'สดใส', matrix: mulMatrix(con(1.08), sat(1.45)) },
  { id: 'fresh', name: 'ผลไม้สด', matrix: mulMatrix(temp(0.25), mulMatrix(con(1.12), sat(1.3))) },
  { id: 'drama', name: 'ดราม่า', matrix: mulMatrix(con(1.35), sat(0.7)) }
];

const clamp = (v: unknown, lo: number, hi: number) => { const n = Number(v); return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : 0; };

/** settings read from a saved work: unknown or broken values become "no change" (never a reason to refuse opening) */
export function readEdit(raw: unknown): PhotoEdit {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const preset = typeof r.preset === 'string' && PRESETS.some(p => p.id === r.preset) ? r.preset : '';
  return {
    brightness: clamp(r.brightness, -100, 100), contrast: clamp(r.contrast, -100, 100), saturation: clamp(r.saturation, -100, 100),
    warmth: clamp(r.warmth, -100, 100), sharpness: clamp(r.sharpness, 0, 100), blur: clamp(r.blur, 0, 100),
    preset, amount: r.amount === undefined ? 100 : clamp(r.amount, 0, 100)
  };
}

export const isNoEdit = (e: PhotoEdit) =>
  !e.brightness && !e.contrast && !e.saturation && !e.warmth && !e.sharpness && !e.blur && (!e.preset || !e.amount);

/** whether a picture's saved `photoEdit` changes how it looks (the "แต่งแล้ว" mark on the page, its chip, the layer list) */
export const isEdited = (raw: unknown) => !isNoEdit(readEdit(raw));

/** a look at strength t (0…1): the identity blended towards its matrix */
export const mixMatrix = (m: Matrix, t: number): Matrix => m.map((v, k) => I[k] + (v - I[k]) * t);

/** Fabric filter descriptions (the same shape Fabric saves), in drawing order */
export type FilterSpec =
  | { type: 'ColorMatrix'; matrix: Matrix; colorsOnly: boolean }
  | { type: 'Brightness'; brightness: number }
  | { type: 'Contrast'; contrast: number }
  | { type: 'Saturation'; saturation: number }
  | { type: 'Convolute'; matrix: number[]; opaque: boolean }
  | { type: 'Blur'; blur: number };

export function filterSpecs(e: PhotoEdit): FilterSpec[] {
  const out: FilterSpec[] = [];
  const look = PRESETS.find(p => p.id === e.preset);
  if (look && e.amount) out.push({ type: 'ColorMatrix', matrix: mixMatrix(look.matrix, e.amount / 100), colorsOnly: true });
  if (e.brightness) out.push({ type: 'Brightness', brightness: e.brightness / 250 });   // ±0.4
  if (e.contrast) out.push({ type: 'Contrast', contrast: e.contrast / 200 });          // ±0.5
  if (e.saturation) out.push({ type: 'Saturation', saturation: e.saturation / 100 });  // ±1 (−1 = grey)
  if (e.warmth) out.push({ type: 'ColorMatrix', matrix: temp(e.warmth / 100), colorsOnly: true });
  if (e.sharpness) {
    const a = (e.sharpness / 100) * 0.6;
    out.push({ type: 'Convolute', matrix: [0, -a, 0, -a, 1 + 4 * a, -a, 0, -a, 0], opaque: false });
  }
  if (e.blur) out.push({ type: 'Blur', blur: (e.blur / 100) * 0.3 });
  return out;
}

/** the largest side Fabric's GPU filters handle in one piece here (fabric.textureSize); bigger pictures are made smaller first */
export const EDIT_MAX_SIDE = 4096;
export function editSize(w: number, h: number): { w: number; h: number; scale: number } {
  const s = Math.min(1, EDIT_MAX_SIDE / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * s)), h: Math.max(1, Math.round(h * s)), scale: s };
}

/** the server's size limit for one picture (worker/rules.ts MAX_IMAGE_BYTES — the test keeps them equal) */
export const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
/**
 * A picture made smaller keeps its see-through parts: PNG/WebP (by the file's own type when it is still on this device,
 * by the server address otherwise) is written as PNG, anything else as JPEG. A blob: address has no extension, so the
 * type must come from the file — judging by the address turned a transparent logo's background black.
 */
export function shrinkType(fileType: string | undefined, src: string): 'image/png' | 'image/jpeg' {
  const t = (fileType ?? '').toLowerCase();
  if (t) return t === 'image/png' || t === 'image/webp' || t === 'image/gif' ? 'image/png' : 'image/jpeg';
  return /\.(png|webp|gif)($|\?)/i.test(src) || /^data:image\/(png|webp|gif)/i.test(src) ? 'image/png' : 'image/jpeg';
}
